import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import OffseasonNotice from '../../Shared/OffseasonNotice';
import { loadBattlePrefs, saveBattlePrefs } from '../../../utils/battlePreferences';
import styles from './Duel.module.css';

const Duel: React.FC = () => {
  const { mercenary } = useMercenary();
  const [maxLevel, setMaxLevel] = useState<number>(mercenary ? mercenary.level + 2 : 1);
  const [strategy, setStrategy] = useState<string>('Normal');
  const [surrenderHP, setSurrenderHP] = useState<number>(50);
  const [inQueue, setInQueue] = useState<boolean>(false);
  const [healMessage, setHealMessage] = useState<string>('');
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const toggleShowMore = () => setShowMore(!showMore);

  useEffect(() => {
    if (mercenary) {
      const prefs = loadBattlePrefs(mercenary.id, 'duel', {
        strategy: 'Normal',
        surrenderHP: 50,
        maxLevel: mercenary.level + 2,
      });
      setStrategy(prefs.strategy as string);
      setSurrenderHP(prefs.surrenderHP as number);
      setMaxLevel((prefs.maxLevel as number) ?? mercenary.level + 2);
      checkQueueStatus();
      checkHealStatus();
    }
  }, [mercenary]);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const checkQueueStatus = async () => {
    try {
      const response = await axios.get(`/api/duel/queue/status/${mercenary.id}`);
      if (response.data.inQueue) {
        setInQueue(true);
        setStrategy(response.data.strategy);
        setSurrenderHP(response.data.surrenderHP * 100);
        setMaxLevel(response.data.maxLevel);
      } else {
        setInQueue(false);
      }
    } catch {
      displayToast('Error checking queue status.', 'error');
    }
  };

  const checkHealStatus = async () => {
    try {
      const response = await axios.get(`/api/duel/heal-status/${mercenary.id}`);
      setHealMessage(response.data.message);
    } catch {
      displayToast('Error checking heal status.', 'error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mercenary) {
      displayToast('No mercenary selected.', 'error');
      return;
    }

    try {
      const response = await axios.post(
        '/api/battle/duel/queue',
        { mercenaryId: mercenary.id, strategy, surrenderHP: surrenderHP / 100, minLevel: mercenary.level, maxLevel: maxLevel || mercenary.level + 2 },
        { withCredentials: true }
      );

      if (response.data.battleId) {
        navigate(`/battle-result/${response.data.battleId}`);
      } else {
        setInQueue(true);
        displayToast(response.data.message || 'Queued for a duel. Waiting for an opponent.', 'success');
      }
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Error queuing for duel. Please try again later.', 'error');
    }
  };

  const handleLeaveQueue = async () => {
    try {
      await axios.post(`/api/duel/queue/leave/${mercenary.id}`, {}, { withCredentials: true });
      setInQueue(false);
      displayToast('Left the queue.', 'success');
    } catch {
      displayToast('Failed to leave the queue.', 'error');
    }
  };

  useEffect(() => {
    if (mercenary) {
      saveBattlePrefs(mercenary.id, 'duel', {
        strategy,
        surrenderHP,
        maxLevel,
      });
    }
  }, [mercenary, strategy, surrenderHP, maxLevel]);

  if (!mercenary) {
    return <p className={styles.errorMessage}>No mercenary available. Please select a mercenary.</p>;
  }

  return (
    <OffseasonNotice feature="duels">
      <div className={styles.duelContainer}>
        {toastMessage && (
          <Toast
            message={toastMessage}
            type={toastType}
            duration={3000}
            onClose={() => setToastMessage(null)}
          />
        )}
        
        <div className={styles.headerSection}>
          <h1 className={styles.title}>The Grand Arena</h1>
          <p className={styles.subtitle}>Test your mettle against fellow mercenaries</p>
        </div>

        <div className={styles.loreSection}>
          <p className={styles.introText}>
            In the heart of Njordheim, beneath the shadow of the Emperor's citadel, lies the grand arena. Here, champions are forged in blood and steel.
          </p>
          {showMore && (
            <div className={styles.expandedLore}>
              <p>Duels are fought with honor and cunning. Victory brings glory and rewards, while defeat offers harsh lessons.</p>
              <p>Each combatant sets their surrender threshold, ensuring they live to fight another day.</p>
              <p>The duel system operates on a waiting queue. Prepare your strategy, set your limits, and step into the sands.</p>
            </div>
          )}
          <button className={styles.toggleTextButton} onClick={toggleShowMore}>
            {showMore ? 'Hide Lore' : 'Read Lore'}
          </button>
        </div>

        <div className={styles.mainContent}>
          <div className={styles.actionPanel}>
            {!inQueue ? (
              <form onSubmit={handleSubmit} className={styles.duelForm}>
                <h3 className={styles.panelTitle}>Combat Preparations</h3>
                
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="strategy" className={styles.label}>Combat Strategy</label>
                    <div className={styles.selectWrapper}>
                      <select id="strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)} className={styles.formControl}>
                        <optgroup label="Balanced">
                          <option value="Normal">Normal</option>
                          <option value="Normal: Light Attacks">Normal: Light Attacks</option>
                          <option value="Normal: Heavy Attacks">Normal: Heavy Attacks</option>
                        </optgroup>
                        <optgroup label="Aggressive">
                          <option value="Offensive">Offensive</option>
                          <option value="Offensive: Light Attacks">Offensive: Light Attacks</option>
                          <option value="Offensive: Heavy Attacks">Offensive: Heavy Attacks</option>
                        </optgroup>
                        <optgroup label="Cautious">
                          <option value="Defensive">Defensive</option>
                          <option value="Defensive: Light Attacks">Defensive: Light Attacks</option>
                          <option value="Defensive: Heavy Attacks">Defensive: Heavy Attacks</option>
                        </optgroup>
                        <optgroup label="Reckless">
                          <option value="Berserk">Berserk</option>
                          <option value="Berserk: Light Attacks">Berserk: Light Attacks</option>
                          <option value="Berserk: Heavy Attacks">Berserk: Heavy Attacks</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="surrenderHP" className={styles.label}>Surrender Threshold: {surrenderHP}% HP</label>
                    <input 
                      type="range" 
                      id="surrenderHP" 
                      min="0" 
                      max="90" 
                      step="10" 
                      value={surrenderHP} 
                      onChange={(e) => setSurrenderHP(parseInt(e.target.value))} 
                      className={styles.rangeControl}
                      style={{ '--val': `${Math.round((surrenderHP / 90) * 100)}%` } as React.CSSProperties}
                    />
                    <div className={styles.rangeLabels}>
                      <span>Death (0%)</span>
                      <span>Cautious (90%)</span>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="maxLevel" className={styles.label}>Max Opponent Level</label>
                    <div className={styles.numberInputWrapper}>
                      <button type="button" onClick={() => setMaxLevel(Math.max(mercenary.level, maxLevel - 1))} className={styles.numberBtn}>-</button>
                      <input 
                        id="maxLevel" 
                        type="number" 
                        value={maxLevel || ''} 
                        placeholder={String(mercenary.level + 2)}
                        min={mercenary.level} 
                        max={mercenary.level + 5} 
                        onChange={(e) => { const v = e.target.value; setMaxLevel(v === '' ? 0 : parseInt(v) || 0); }} 
                        className={styles.numberControl} 
                        required 
                      />
                      <button type="button" onClick={() => setMaxLevel(Math.min(mercenary.level + 5, maxLevel + 1))} className={styles.numberBtn}>+</button>
                    </div>
                  </div>
                </div>

                {healMessage && <div className={styles.healMessageBox}><p>{healMessage}</p></div>}

                <div className={styles.submitContainer}>
                  <button type="submit" className={styles.submitButton}>
                    <span className={styles.buttonText}>Enter the Arena</span>
                    <span className={styles.buttonGlow}></span>
                  </button>
                </div>
              </form>
            ) : (
              <div className={styles.queueInfo}>
                <div className={styles.pulsingCircle}></div>
                <h3 className={styles.queueTitle}>Seeking Worthy Opponent...</h3>
                
                <div className={styles.queueDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Strategy:</span>
                    <span className={styles.detailValue}>{strategy}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Surrender At:</span>
                    <span className={styles.detailValue}>{surrenderHP}% HP</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Max Level:</span>
                    <span className={styles.detailValue}>{maxLevel}</span>
                  </div>
                </div>
                
                {healMessage && <p className={styles.healMessage}>{healMessage}</p>}
                
                <button className={styles.leaveButton} onClick={handleLeaveQueue}>
                  Withdraw from Queue
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </OffseasonNotice>
  );
};

export default Duel;
